import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiMapPin, FiVideo, FiCheck, FiX } from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import Pagination from '../../components/Pagination/Pagination';
import styles from '../../styles/adminPanels.module.css';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { fetchAllLeads, updateLeadStatus } from '../../features/leads/leadsSlice';
import { LeadStatus } from '../../types';

const STATUS_COLORS: Record<string, string> = { pending: '#e8a86a', confirmed: '#1d60f6', rejected: '#e2534a' };
const STATUS_FILTERS: { label: string; value: LeadStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Rejected', value: 'rejected' },
];
const PAGE_SIZE = 8;

const AdminMeetings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { allLeads, status } = useAppSelector((state) => state.leads);
  const [activeFilter, setActiveFilter] = useState<LeadStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [contactByMap, setContactByMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const filters: { status?: string; leadType?: string } = { leadType: 'meeting' };
    if (activeFilter !== 'all') filters.status = activeFilter;
    dispatch(fetchAllLeads(filters));
    setPage(1);
  }, [dispatch, activeFilter]);

  const handleConfirm = (id: string) => {
    const contactBy = contactByMap[id];
    if (!contactBy) { toast.warning('Pick a "contact by" date & time first.'); return; }
    dispatch(updateLeadStatus({ id, status: 'confirmed', contactBy: new Date(contactBy).toISOString() }));
    toast.success('Meeting confirmed.');
  };
  const handleReject = (id: string) => {
    dispatch(updateLeadStatus({ id, status: 'rejected' }));
    toast.info('Meeting request rejected.');
  };

  const totalPages = Math.max(1, Math.ceil(allLeads.length / PAGE_SIZE));
  const paged = allLeads.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AdminShell>
      <PageHeader title="Meetings" subtitle="Meeting requests from the landing page and user dashboards." action={<AdminWalletBadge />} />
      <div className={styles.page}>
        <div className={styles.filters} style={{ marginBottom: 18 }}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              className={`${styles.filterBtn} ${activeFilter === f.value ? styles.filterBtnActive : ''}`}
              onClick={() => setActiveFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {status === 'loading' && <div className={styles.loading}><span className={styles.spinnerLg} /></div>}
        {status !== 'loading' && allLeads.length === 0 && <div className={styles.empty}>No meeting requests match this filter.</div>}

        {status !== 'loading' && paged.map((lead) => (
          <div key={lead.id} style={{ background: 'rgba(23,22,26,.5)', border: '1px solid var(--line)', borderRadius: 14, padding: '18px 20px', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
              <b style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                {lead.meeting_type === 'vc' ? <FiVideo /> : <FiMapPin />}
                {lead.full_name} — {lead.meeting_type === 'vc' ? 'Virtual / VC call' : 'In-person meeting'}
              </b>
              <span className={styles.statusPill} style={{ color: STATUS_COLORS[lead.status], borderColor: STATUS_COLORS[lead.status] }}>
                {lead.status}
              </span>
            </div>
            <div className={styles.subCell}>
              {lead.email}{lead.phone ? ` · ${lead.phone}` : ''}{lead.fund_org ? ` · ${lead.fund_org}` : ''} · {new Date(lead.created_at).toLocaleString()}
            </div>
            {lead.message && <p style={{ fontSize: 13, marginTop: 10, color: 'var(--cream)' }}>{lead.message}</p>}

            {lead.status === 'confirmed' && lead.contact_by && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--copper-light)' }}>
                We'll contact by {new Date(lead.contact_by).toLocaleString()}
              </div>
            )}

            {lead.status === 'pending' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                <input
                  type="datetime-local"
                  className={styles.filterBtn}
                  style={{ padding: '9px 12px', color: 'var(--cream)' }}
                  value={contactByMap[lead.id] || ''}
                  onChange={(e) => setContactByMap((prev) => ({ ...prev, [lead.id]: e.target.value }))}
                />
                <button className={styles.actionBtn} onClick={() => handleConfirm(lead.id)}><FiCheck /> Confirm</button>
                <button className={styles.actionBtnReject} onClick={() => handleReject(lead.id)}><FiX /> Reject</button>
              </div>
            )}
          </div>
        ))}

        {status !== 'loading' && allLeads.length > 0 && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>
    </AdminShell>
  );
};

export default AdminMeetings;
